import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { ScoreCard } from "@/components/ScoreCard";
import { userFinancials } from "@/lib/mock-data";
import { usd } from "@/lib/format";
import { TrendingUp, PiggyBank, CreditCard, Target, Sparkles } from "lucide-react";

export const Route = createFileRoute("/health")({
  head: () => ({ meta: [{ title: "Financial Health — LendyTrack" }] }),
  component: Health,
});

const roadmap = [
  {
    icon: CreditCard,
    title: "Reduce credit card utilization",
    detail: "Pay down balances to under 30% of available credit.",
    impact: "+35 FICO points",
    timeline: "30–45 days",
    progress: 22,
  },
  {
    icon: PiggyBank,
    title: "Build a $1,000 emergency fund",
    detail: "Auto-transfer $42/week into a high-yield savings account.",
    impact: "Avoid future payday loans",
    timeline: "16 weeks",
    progress: 32,
  },
  {
    icon: Target,
    title: "Choose a debt-paydown strategy",
    detail: "Avalanche saves the most interest. Snowball builds momentum.",
    impact: "Pay off debt 14 months faster",
    timeline: "Start today",
    progress: 0,
  },
  {
    icon: TrendingUp,
    title: "Wait 90 days before applying",
    detail: "Hard inquiries hurt approval odds. Pause new credit applications.",
    impact: "+12 approval odds",
    timeline: "90 days",
    progress: 10,
  },
];

function Health() {
  const cashflow = userFinancials.monthlyIncome - userFinancials.monthlyExpenses;
  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Financial health</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Your borrowing readiness</h1>
        <p className="mt-1 text-sm text-muted-foreground">Improve before you apply. Small changes unlock big rate differences.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <ScoreCard label="Credit Score" score={Math.round((userFinancials.creditScore / 850) * 100)} caption={`FICO ${userFinancials.creditScore}`} />
        <ScoreCard label="Financial Wellness" score={userFinancials.financialWellness} />
        <ScoreCard label="Loan Readiness" score={userFinancials.loanReadiness} caption="Target: 75+" />
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Monthly cashflow</div>
          <div className={`mt-1 font-display text-2xl font-semibold ${cashflow > 0 ? "text-success" : "text-danger"}`}>{usd(cashflow)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Credit utilization</div>
          <div className="mt-1 font-display text-2xl font-semibold text-warning-foreground">{userFinancials.creditUtilization}%</div>
          <div className="text-[11px] text-muted-foreground">Target: under 30%</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Total debt</div>
          <div className="mt-1 font-display text-2xl font-semibold">{usd(userFinancials.totalDebt)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Emergency savings</div>
          <div className="mt-1 font-display text-2xl font-semibold">{usd(userFinancials.emergencySavings)}</div>
          <div className="text-[11px] text-muted-foreground">Target: {usd(1000)}</div>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-accent">
          <Sparkles className="h-3 w-3" /> Improvement roadmap
        </div>
        <h2 className="mt-1 font-display text-2xl font-semibold">90-day plan</h2>

        <div className="mt-5 space-y-4">
          {roadmap.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={step.title} className="grid gap-4 rounded-xl border border-border bg-card p-5 shadow-soft md:grid-cols-[64px_1fr_220px]">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-accent text-accent-foreground">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Step {i + 1}</span>
                    <div className="font-display text-lg font-semibold">{step.title}</div>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{step.detail}</p>
                  <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-accent" style={{ width: `${step.progress}%` }} />
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-background p-3 text-xs">
                  <div className="text-muted-foreground">Estimated impact</div>
                  <div className="mt-0.5 font-semibold text-success">{step.impact}</div>
                  <div className="mt-2 text-muted-foreground">Timeline</div>
                  <div className="mt-0.5 font-semibold">{step.timeline}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-gradient-hero p-6 text-primary-foreground shadow-soft">
        <div className="text-xs uppercase tracking-widest text-accent">Why this matters</div>
        <p className="mt-2 max-w-3xl text-sm text-primary-foreground/85">
          Completing this plan could move you from a 648 FICO to ~683 in 90 days.
          On a $10,000 personal loan, that's roughly $1,840 in lifetime interest savings —
          and it unlocks lenders that won't touch subprime borrowers today.
        </p>
      </div>
    </AppShell>
  );
}
